import { AfterContentInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EditAddressViewModel,
  EditContactViewModel,
  EditQualifiedPersonCommand,
  IEditAddressViewModel,
  IEditContactViewModel,
  LegalEntityStatus,
  QualifiedPersonViewModel
} from '@services/cleanfill-api/cleanfill-api.generated';
import { ModalService } from '@services/cleanfill-core/modal/modal.service';
import { StoreRegistrationService } from '@services/cleanfill-core/store-registration/store-registration.service';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RegistrationCompleteComponent } from '../modals/registration-complete/registration-complete.component';

@Component({
  selector: 'qp-registration',
  templateUrl: './qp-registration.component.html',
  styleUrls: ['./qp-registration.component.scss']
})
export class QpRegistrationComponent implements OnInit, OnDestroy, AfterContentInit {
  id: string;
  currentIndex = 1;
  currentForm: AbstractControl;
  qp$: Observable<QualifiedPersonViewModel>;
  title: string;
  form: FormGroup;
  subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private registrationStore: StoreRegistrationService,
    private modal: ModalService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    if (this.route?.snapshot) {
      this.id = this.route.snapshot.params.id;
      this.registrationStore.getQualifiedPerson(this.id);
    }
    this.initForm();
    this.qp$ = registrationStore.qp$;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.qp$.pipe(filter(qp => !!qp)).subscribe(async qp => {
        if (qp.status === LegalEntityStatus.Active) {
          await this.router.navigateByUrl('/');
        } else {
          this.patchQp(qp);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  ngAfterContentInit(): void {
    this.cdr.detectChanges();
  }

  initForm(): void {
    this.form = this.fb.group({
      uniqueId: '',
      status: '',
      legalEntityType: '',
      businessType: this.fb.group({
        name: ''
      }),
      businessInfoForm: this.fb.group({
        isIndividual: true,
        pgoNumber: ['', Validators.maxLength(100)],
        name: ['', Validators.maxLength(100)],
        address: this.getAddressFormGroup(),
        municipalityName: [''],
        phoneNumber: ['', [Validators.required, Validators.pattern(/^(\+\d{1,2}\s*)?\(?\d{3}\)?[\s.-]*\d{3}[\s.-]*\d{4}$/)]],
        extension: ['', Validators.pattern(/^\d{0,5}$/)]
      }),
      personalInfo: this.fb.group({
        contact: this.getContactFormGroup()
      })
    });
    const businessType = this.form.get('businessType');
    businessType.markAsTouched();
    this.currentForm = this.form.get('businessInfoForm');
  }
  getContactFormGroup() {
    return this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^(\+\d{1,2}\s*)?\(?\d{3}\)?[\s.-]*\d{3}[\s.-]*\d{4}$/)]],
      extension: ['', Validators.pattern(/^\d{0,5}$/)]
    });
  }

  getAddressFormGroup() {
    return this.fb.group({
      postalCode: ['', [Validators.required, Validators.pattern(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/)]],
      line1: ['', Validators.required],
      provinceUniqueId: [undefined, Validators.required],
      city: ['', Validators.required]
    });
  }

  patchQp(qp: QualifiedPersonViewModel) {
    const qpInfo = {
      businessInfoForm: {
        isIndividual: !!qp?.isIndividual,
        pgoNumber: qp?.pgoNumber ?? '',
        name: qp?.name ?? '',
        address: qp?.address ?? '',
        municipalityName: qp?.municipalityName ?? '',
        phoneNumber: qp?.phoneNumber ?? '',
        extension: qp?.extension ?? ''
      },
      personalInfo: {
        contact: qp?.contact ?? ''
      }
    };
    this.form.patchValue(qpInfo);
  }

  panelChanged(index: number): void {
    switch (this.currentIndex) {
      case 1:
        // save company
        this.saveQp();
        break;
      case 2:
        // save company
        // this.saveContact();
        break;
      default:
        break;
    }
    this.currentIndex = index;
    if (index === 0) {
    }
    switch (index) {
      case 0:
        this.router.navigate(['register', 'business-type', 'qp', this.id]);
        break;
      case 1:
        this.currentForm = this.form.get('businessInfoForm');
        break;
      case 2:
        this.currentForm = this.form.get('personalInfo');
        break;
      default:
        break;
    }
  }

  saveQp() {
    if (this.currentForm.untouched || this.currentForm.invalid) {
      return;
    }
    const formValue = this.form.value;
    const { businessInfoForm } = formValue;
    const addressModel: IEditAddressViewModel = { ...businessInfoForm.address };
    const qpModel: EditQualifiedPersonCommand = new EditQualifiedPersonCommand({
      uniqueId: this.id,
      address: new EditAddressViewModel(addressModel),
      isIndividual: businessInfoForm.isIndividual,
      pgoNumber: businessInfoForm.pgoNumber,
      name: businessInfoForm.name || 'undefined', // TODO : Remove after BE fix
      phoneNumber: businessInfoForm.phoneNumber,
      extension: businessInfoForm.extension,
      municipalityName: businessInfoForm.municipalityName,
      status: LegalEntityStatus.Draft
    });
    this.registrationStore.editQualifiedPerson(this.id, qpModel);
  }

  submit() {
    const formValue = this.form.value;
    const { businessInfoForm, personalInfo } = formValue;
    const addressModel: IEditAddressViewModel = { ...businessInfoForm.address };
    const contactModel: IEditContactViewModel = { ...personalInfo.contact };
    const qpModel: EditQualifiedPersonCommand = new EditQualifiedPersonCommand({
      uniqueId: this.id,
      address: new EditAddressViewModel(addressModel),
      contact: new EditContactViewModel(contactModel),
      isIndividual: businessInfoForm.isIndividual,
      pgoNumber: businessInfoForm.pgoNumber,
      name: businessInfoForm.name || 'undefined', // TODO : Remove after BE fix
      phoneNumber: businessInfoForm.phoneNumber,
      extension: businessInfoForm.extension,
      municipalityName: businessInfoForm.municipalityName,
      status: LegalEntityStatus.Active
    });
    this.registrationStore.editQualifiedPerson(this.id, qpModel, async () => {
      this.form.markAsPristine();
      await this.modal.open(RegistrationCompleteComponent, {}).toPromise();
    });
  }
}
